import React, {useEffect, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import Button from "../../../../../components/button/button.jsx";
import {useParams, useSearchParams} from "react-router";
import projectUtils from "../../../../../utils/project-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import materialGroupService from "../../../../../services/web/material-group-service.js";
import {FaPlus} from "react-icons/fa";
import useFetchOptions from "../../hooks/use-fetch-options.js";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import materialRegisterService from "../../../../../services/web/material-register-service.js";
import materialXStorageService from "../../../../../services/web/material-x-storage-service.js";
import materialService from "../../../../../services/web/material-service.js";
import materialSupplierService from "../../../../../services/web/material-supplier-service.js";
import InputDateTime from "../../../../../components/input/input-datetime.jsx";
import {format} from "date-fns";
import Attachs from "./components/attachs.jsx";
import materialUniqueService from "../../../../../services/web/material-unique-service.js";
import Alert from "../../../../../components/alert/alert.jsx";
import ProjectUtils from "../../../../../utils/project-utils.js";

const initialData = {
    id: "",
    storage_id: "",
    group: "",
    material_id: "",
    invoice: "",
    supplier_id: "",
    quantity: "",
    serial: "",
    asset: "",
    due_date: "",
}

const initialWeaponData = {
    register: "",
    date: "",
    expiration: "",
    country: "",
    specie: "",
    number: "",
    sinarm: "",
    uf: "",
    expeditor: "",
    date_expedition: "",
    caliber: "",
    shooting: "",
    operation: "",
    finish: "",
    barrel_quant: "",
    barrel_length: "",
    soul_type: "",
    streak_quant: "",
    streak_direction: "",
}

const MaterialRegistersForm = () => {
    const navigate = navigateHook();
    const [searchParams] = useSearchParams();
    const {id} = useParams()
    const [isLoading, setIsLoading] = useState(true)
    const [isCopying, setIsCopying] = useState(true)
    const [data, setData] = useState(initialData)
    const [weaponData, setWeaponData] = useState(initialWeaponData)
    const [attachList, setAttachList] = useState([])
    const {storageByUserOptions} = useFetchOptions({fetchStorageByUser: true})
    const [decreaseDate, setDecreaseDate] = useState("")

    const onChange = ({target: {value, id}}) => setData(prev => ({...prev, [id]: value}))
    const onChangeWeapon = ({target: {value, id}}) => setWeaponData(prev => ({...prev, [id]: value}))
    const onChangeSelect = (id, value) => setData(prev => ({...prev, [id]: value || ""}));
    const handleDate = (id, value, isWeaponData = true) => {
        const dateFormated = value ? format(value, "yyyy-MM-dd") : null
        if (isWeaponData) {
            setWeaponData(prev => ({...prev, [id]: dateFormated}))
        } else {
            setData(prev => ({...prev, [id]: dateFormated}))
        }
    }

    useEffect(() => {
        if (!id) setIsCopying(false)
    }, [id]);

    const cpParam = searchParams.get("cp");
    const registerId = cpParam || id;

    const {data: register, refetch, fetchStatus} = useQuery({
        queryKey: ['material-register', registerId],
        queryFn: () =>  materialRegisterService.getById(registerId),
        enabled: !!registerId,
    });

    useEffect(() => {
        if (fetchStatus === "idle" && register) {
            handleData(register).then(() => {})
        }
    }, [fetchStatus, register]);

    const handleData = async (data) => {
        const { storage_id, material_id, invoice, supplier_id, quantity, unique_id, acquisition_attaches, due_date, unique } = data
        setIsCopying(true)

        const {group_id} = await materialService.getById(material_id)
        let newData = { storage_id, material_id, invoice, supplier_id, quantity, group: group_id, due_date}

        if (unique) {
            const {asset, details, serial, movement_decrease} = unique
            newData = {
                ...newData,
                asset,
                serial
            }
            setWeaponData(details || initialWeaponData)

            if (movement_decrease) {
                setDecreaseDate(movement_decrease.date)
            }
        }

        const registerNormalized = projectUtils.normalizeData(newData)
        setData(registerNormalized)

        let attachs = []
        if (acquisition_attaches) {
            attachs = JSON.parse(acquisition_attaches).map((a, index) => ({
                id: index,
                description: a.description,
                file: "",
                attachId: a.attachId
            }))
        }
        setAttachList(attachs)
        setTimeout(() => setIsCopying(false), 1000)
    }

    const {data: groups, isLoading: isLoadingGroups} = useQuery({
        queryKey: ['material-groups', data.storage_id],
        queryFn: () => materialGroupService.options(data.storage_id),
        enabled: !!data.storage_id
    });

    useEffect(() => {
        if (isCopying) return
        const groupSelected = groups && groups.length === 1 ? groups[0].value : ""
        setData(prev => ({...prev, group: groupSelected}))
    }, [groups]);

    const {data: materials, isLoading: isLoadingMaterials} = useQuery({
        queryKey: ['materials', data.group],
        queryFn: () => materialXStorageService.optionsByGroup(data.group),
        enabled: !!data.group
    });

    useEffect(() => {
        if (register && materials) {
            const find = materials.find(m => m.value === register.material_id)
            if (!find) {
                materials.push({value: register.material_id, label: register.material_name, material_id: undefined})
            }
        }
    }, [register, materials]);

    useEffect(() => {
        if (isCopying) return
        setData(prev => ({...prev, material_id: ""}))
    }, [materials]);

    const {data: materialSelected, isLoading: isLoadingMaterial} = useQuery({
        queryKey: ['material', data.material_id],
        queryFn: async () => materialService.getById(data.material_id),
        enabled: !!data.material_id
    });

    const {data: suppliers, isLoading: isLoadingSuppliers} = useQuery({
        queryKey: ['suppliers'],
        queryFn: () => materialSupplierService.options(),
    });

    const saveCopy = () => save(false, true)
    const saveAdd = () => save(true)

    const save = (add = false, copy = false) => {
        setIsLoading(true);

        const formData = new FormData()

        Object.keys(data).forEach(key => {
            formData.append(key, data[key] ?? "")
        });

        if (materialSelected?.is_unique)  formData.append('weapon_data', JSON.stringify(weaponData));

        for (const attach of attachList) {
            const {id, description, file, attachId} = attach
            if (!attachId && file) {
                formData.append(`acquisitionDescription[${id}]`, description);
                formData.append(`acquisitionAttach[${id}]`, file);
            }
        }

        const oldAttachment = attachList.filter(a => a.attachId);
        if (oldAttachment.length) formData.append("attachment", JSON.stringify(oldAttachment));

        materialRegisterService.save(formData, id)
            .then(({data}) => {
                if (id) {
                    ProjectUtils.success(false);
                    refetch();
                    return;
                }

                if (add) {
                    ProjectUtils.success(false, "Salva com sucesso");
                    clearData();
                    setIsLoading(false);
                    return;
                }

                if (copy) {
                    navigate(`/materials/registers/form/?cp=${data.id}`);
                    ProjectUtils.success(false, "Salva com sucesso");
                    return;
                }

                ProjectUtils.success(true);
                navigate(`/materials/registers/form/${data.id}`);
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }
                setIsLoading(false)
                projectUtils.swalErrorMessage('', message, 'Ok');
            })
    }

    const clearData = () => {
        setData(initialData)
        setWeaponData(initialWeaponData)
        setAttachList([])
        setDecreaseDate("")
    }

    const openForm = () => {
        const urlDev = `/materials/registers/form`;
        clearData()

        navigate(urlDev)
    }

    useEffect(() => {
        setIsLoading(isLoadingGroups || isLoadingMaterials || isLoadingMaterial || isLoadingSuppliers || isCopying)
    }, [isLoadingGroups, isLoadingMaterials, isLoadingMaterial, isLoadingSuppliers, isCopying]);

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Aquisição
                </Template.Title>
                {
                    id &&
                    <Button target={"primary-outline"} onClick={openForm}>
                        <FaPlus/><span className="font-bold">Adicionar aquisição</span>
                    </Button>
                }
            </Template.Header>
            <Template.Body className={isLoading ? "hidden" : ""}>
                <Card>
                    <Card.Body>
                        {
                            decreaseDate &&
                            <Alert target={"warning"} className={"mb-3"}>
                                Material baixado em {decreaseDate}
                            </Alert>
                        }
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-2">
                                <SelectComponent
                                    isDisabled={!!id}
                                    id="storage_id"
                                    name="storage_id"
                                    value={data.storage_id}
                                    options={storageByUserOptions || []}
                                    label={"Depósito *"}
                                    onChange={(selected) => onChangeSelect("storage_id", selected?.value)}
                                />
                            </div>
                            <div className="col-span-2">
                                <SelectComponent
                                    isDisabled={!!id}
                                    id="group"
                                    name="group"
                                    value={data.group}
                                    options={groups || []}
                                    label={"Grupo *"}
                                    onChange={(selected) => onChangeSelect("group", selected?.value)}
                                />
                            </div>
                            <div className="col-span-4">
                                <SelectComponent
                                    isDisabled={!!id}
                                    id="material_id"
                                    name="material_id"
                                    value={data.material_id}
                                    options={materials || []}
                                    label={"Material *"}
                                    onChange={(selected) => onChangeSelect("material_id", selected?.value)}
                                />
                            </div>
                            <div className="col-span-4">
                                {
                                    materialSelected &&
                                    <>
                                        {
                                            !materialSelected.is_unique &&
                                            <InputForm disabled={!!id} type={"number"} id="quantity" value={data.quantity} label="Quantidade" onChange={onChange}/>
                                        }

                                        {
                                            materialSelected.is_unique &&
                                            <div className="grid grid-cols-2 gap-4">
                                                <InputForm disabled={!!id} id="serial" value={data.serial} label="Número de série" onChange={onChange}/>
                                                <InputForm disabled={!!id} id="asset" value={data.asset} label="Número de patrimônio" onChange={onChange}/>
                                            </div>
                                        }
                                    </>
                                }
                            </div>

                            <div className="col-span-3">
                                <InputDateTime
                                    id="due-date"
                                    label="Data de vencimento"
                                    selected={data.due_date}
                                    dateFormat={"L"}
                                    disabled={!!id}
                                    onChange={(date) => handleDate("due_date", date, false)}
                                />
                            </div>

                            <div className="col-span-3">
                                <InputForm
                                    disabled={!!id}
                                    id="invoice"
                                    value={data.invoice}
                                    label="Nota fiscal"
                                    onChange={onChange}/>
                            </div>
                            <div className="col-span-6">
                                <SelectComponent
                                    isDisabled={!!id}
                                    id="supplier_id"
                                    name="supplier_id"
                                    value={data.supplier_id}
                                    options={suppliers || []}
                                    label={"Fornecedor"}
                                    onChange={(selected) => onChangeSelect("supplier_id", selected?.value)}
                                />
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {
                    materialSelected?.is_gun &&
                    <Card className={"mt-2"}>
                        <Card.Header>
                            <Card.Title>Especificações</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        id="register"
                                        value={weaponData.register}
                                        label="Registro da arma"
                                        onChange={onChangeWeapon}/>
                                </div>
                                <div className="col-span-2">
                                    <InputDateTime
                                        disabled={!!decreaseDate}
                                        id={"date"}
                                        selected={weaponData.date}
                                        label={"Data registro"}
                                        dateFormat={"L"}
                                        onChange={(date) => handleDate("date", date)}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <InputDateTime
                                        disabled={!!decreaseDate}
                                        id={"expiration"}
                                        selected={weaponData.expiration}
                                        label={"Validade registro"}
                                        dateFormat={"L"}
                                        onChange={(date) => handleDate("expiration", date)}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        id="country"
                                        value={weaponData.country}
                                        label="País de fabricação"
                                        onChange={onChangeWeapon}/>
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        id="specie"
                                        value={weaponData.specie}
                                        label="Espécie"
                                        onChange={onChangeWeapon}/>
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        id="number"
                                        value={weaponData.number}
                                        label="Número da arma"
                                        onChange={onChangeWeapon}/>
                                </div>

                                <div className="col-span-4">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        id="sinarm"
                                        value={weaponData.sinarm}
                                        label="SINARM"
                                        onChange={onChangeWeapon}/>
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        id="uf"
                                        value={weaponData.uf}
                                        label="UF Expedidor (SINARM)"
                                        onChange={onChangeWeapon}/>
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        id="expeditor"
                                        value={weaponData.expeditor}
                                        label="Órgão expedidor (SINARM)"
                                        onChange={onChangeWeapon}/>
                                </div>
                                <div className="col-span-2">

                                    <InputDateTime
                                        disabled={!!decreaseDate}
                                        id={"date_expedition"}
                                        selected={weaponData.date_expedition}
                                        label={"Data da expedição (SINARM)"}
                                        dateFormat={"L"}
                                        onChange={(date) => handleDate("date_expedition", date)}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        id="caliber"
                                        value={weaponData.caliber}
                                        label="Calibre"
                                        onChange={onChangeWeapon}/>
                                </div>

                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        id="shooting"
                                        value={weaponData.shooting}
                                        label="Capacidade de tiros"
                                        onChange={onChangeWeapon}/>
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        id="operation"
                                        value={weaponData.operation}
                                        label="Funcionamento"
                                        onChange={onChangeWeapon}/>
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        id="finish"
                                        disabled={!!decreaseDate}
                                        value={weaponData.finish}
                                        label="Acabamento"
                                        onChange={onChangeWeapon}/>
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        type={"number"}
                                        id="barrel_quant"
                                        value={weaponData.barrel_quant}
                                        label="Quantidade de canos"
                                        onChange={onChangeWeapon}/>
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        type={"number"}
                                        id="barrel_length"
                                        value={weaponData.barrel_length}
                                        label="Comprimento do cano"
                                        onChange={onChangeWeapon}/>
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        id="soul_type"
                                        value={weaponData.soul_type}
                                        label="Tipo da alma"
                                        onChange={onChangeWeapon}/>
                                </div>

                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        type={"number"}
                                        id="streak_quant"
                                        value={weaponData.streak_quant}
                                        label="Quantidade de raias"
                                        onChange={onChangeWeapon}/>
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        disabled={!!decreaseDate}
                                        id="streak_direction"
                                        value={weaponData.streak_direction}
                                        label="Sentido das raias"
                                        onChange={onChangeWeapon}/>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                }

                <Attachs attachList={attachList} setAttachList={setAttachList}/>

                <Card className={"mt-2"}>
                    <Card.Body>
                        <div className="flex justify-end gap-2 w-full">
                            {
                                !id &&
                                <>
                                    <Button target={"primary-outline"} onClick={saveCopy} actionFeedbackType='save'>Salvar e copiar</Button>
                                    <Button target={"primary-outline"} onClick={saveAdd} actionFeedbackType='save'>Salvar e adicionar novo</Button>
                                </>
                            }
                            <Button onClick={() => save(false)} actionFeedbackType='save'>Salvar</Button>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default MaterialRegistersForm;